import debug from 'debug'
import { QRCodeSVG } from 'qrcode.react'
import { type FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import { match } from 'ts-pattern'
import IcBaselineOpenInNew from '~icons/ic/baseline-open-in-new'
import MdiLoading from '~icons/mdi/loading'
import { PayPayLogo } from 'src/components/Payment/PayPayLogo'
import { useGetOrderById } from 'src/services/apollo/payment'
import type {
  OpenPaymentDialogReturns,
  PaymentExecuteFn,
} from 'src/services/helpers/payment'
import { PaymentProvider } from 'src/services/helpers/payment'

const log = debug('pixai:payment:paypay')

interface PaymentConfigCommon {
  orderId: string
}

interface PaymentConfigInlineQRCode extends PaymentConfigCommon {
  mode: 'inline-qrcode'
  content: string
}

interface PaymentConfigRedirect extends PaymentConfigCommon {
  mode: 'redirect'
  url: string
}

type PaymentConfig = PaymentConfigInlineQRCode | PaymentConfigRedirect

interface PaymentDialogContentPayPayProps {
  execute: PaymentExecuteFn
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
}

export const PaymentDialogContentPayPay: FC<
  PaymentDialogContentPayPayProps
> = ({ execute, trackingFnAfterFetch, onSucceeded, onFailed }) => {
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null)

  const { error } = useAsync(async () => {
    const result = await execute({ provider: PaymentProvider.paypay })
    const { orderId, url, codeId } = result
    const urlObj = new URL(url)

    setPaymentConfig({
      orderId,
      ...(codeId
        ? { mode: 'inline-qrcode', content: url }
        : urlObj.searchParams.has('code')
          ? {
              mode: 'inline-qrcode',
              content: urlObj.searchParams.get('code')!,
            }
          : { mode: 'redirect', url }),
    })

    trackingFnAfterFetch?.()
  })

  useEffect(() => {
    if (error) {
      log('Failed to fetch payment configuration', error)
      onFailed?.(error)
    }
  }, [error])

  return (
    <div className="rounded-lg flex items-center justify-center size-full bg-neutral-100">
      {match(paymentConfig)
        .with(null, () => (
          <div className="py-8 size-full text-black/75 flex justify-center text-sm select-none">
            <MdiLoading className="size-6 animate-spin" />
          </div>
        ))
        .with({ mode: 'inline-qrcode' }, p => (
          <PaymentDialogContentPayPayQRCode
            config={p}
            onSucceeded={() =>
              onSucceeded?.({
                orderId: p.orderId,
                finalizedPaymentProvider: PaymentProvider.paypay,
              })
            }
            onFailed={onFailed}
          />
        ))
        .with({ mode: 'redirect' }, p => (
          <PaymentDialogContentPayPayRedirect
            config={p}
            onSucceeded={() =>
              onSucceeded?.({
                orderId: p.orderId,
                finalizedPaymentProvider: PaymentProvider.paypay,
              })
            }
            onFailed={onFailed}
          />
        ))
        .exhaustive()}
    </div>
  )
}

const PaymentDialogContentPayPayQRCode: FC<{
  config: PaymentConfigInlineQRCode
  onSucceeded?: () => void
  onFailed?: (error: Error) => void
}> = ({ config, onSucceeded, onFailed }) => {
  const { t } = useTranslation(['membership'])
  const { data } = useGetOrderById(config.orderId)

  useEffect(() => {
    if (data?.status === 'completed') {
      log('Payment completed', data)
      onSucceeded?.()
    } else if (data?.status === 'cancelled') {
      log('Payment cancelled', data)
      onFailed?.(new Error(t('membership:payment.cancelled-due-to-timeout')))
    } else if (data?.status === 'failed') {
      log('Payment failed', data)
      onFailed?.(new Error(t('membership:payment.failed-generic')))
    }
  }, [data?.status])

  const qrCodeContent = useMemo(() => {
    const url = new URL(config.content)
    if (
      url.hostname === 'www.paypay.ne.jp' &&
      url.pathname === '/app/cashier'
    ) {
      const codeUrl = url.searchParams.get('code')
      if (codeUrl) {
        return codeUrl
      }
    }
    return url.toString()
  }, [config.content])

  return (
    <div className="p-8 flex flex-col items-center gap-4 bg-neutral-100">
      <div className="size-32 bg-gray-600 relative">
        <QRCodeSVG value={qrCodeContent} />

        <div className="absolute inset-0 flex items-center justify-center">
          <PayPayLogo className="w-6 h-6" />
        </div>
      </div>

      <div className="h-px w-full bg-gray-200" />

      <button
        // #FF0033: PayPay Red
        className="text-sm text-black border-2 border-[#FF0033] rounded px-3 py-2 flex items-center gap-2 font-bold bg-white hover:brightness-95 active:brightness-90 select-none"
        onClick={() => window.open(config.content)}
      >
        <PayPayLogo className="w-5 h-5" />

        <span className="leading-none">
          {t('membership:payment.paypay.launch')}
        </span>

        <IcBaselineOpenInNew className="w-5 h-5 ml-1 text-[#FF0033]" />
      </button>

      <div className="flex items-start text-xs gap-2 opacity-80 mt-4">
        <MdiLoading className="size-4 animate-spin-slow shrink-0" />
        <span>{t('membership:payment.paypay.pending-payment-completion')}</span>
      </div>
    </div>
  )
}

const PaymentDialogContentPayPayRedirect: FC<{
  config: PaymentConfigRedirect
  onSucceeded?: () => void
  onFailed?: (error: Error) => void
}> = ({ config, onSucceeded, onFailed }) => {
  const { t } = useTranslation(['membership'])
  const { data } = useGetOrderById(config.orderId)

  useEffect(() => {
    if (data?.status === 'completed') {
      log('Payment completed', data)
      onSucceeded?.()
    } else if (data?.status === 'cancelled') {
      log('Payment cancelled', data)
      onFailed?.(new Error(t('membership:payment.cancelled-due-to-timeout')))
    } else if (data?.status === 'failed') {
      log('Payment failed', data)
      onFailed?.(new Error(t('membership:payment.failed-generic')))
    }
  }, [data?.status])

  return (
    <div className="p-8 flex flex-col items-center gap-4">
      <button
        // #FF0033: PayPay Red
        className="text-sm text-black border-2 border-[#FF0033] rounded px-3 py-2 flex items-center gap-2 font-bold hover:brightness-95 active:brightness-90 select-none"
        onClick={() => window.open(config.url)}
      >
        <PayPayLogo className="w-5 h-5" />

        <span className="leading-none">
          {t('membership:payment.paypay.launch')}
        </span>

        <IcBaselineOpenInNew className="w-5 h-5 ml-1 text-[#FF0033]" />
      </button>

      <div className="flex items-start text-xs gap-2 opacity-80 mt-4">
        <MdiLoading className="size-4 animate-spin-slow shrink-0" />
        <span>{t('membership:payment.paypay.pending-payment-completion')}</span>
      </div>
    </div>
  )
}
